// src/services/appService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { message, notification, Button, Space } from 'antd';
import translateX from 'utils/translateX';
import axiosService from './axiosService';
import dayjs from 'dayjs';
import { logErrorSilent } from 'utils/errorLogger';
import app from 'configs/ConfigIndex';
import utils from 'utils/utils';
import { storage } from 'configs/firebaseAuth';
import { deleteObject, ref } from 'firebase/storage';
import timezones from 'assets/data/timezones.json';
// import { API_PARAMETERS } from 'constants/ApiConstant';
import {
  clone,
  divider,
  genKey,
  onProduction,
  shouldShowLogs,
  sort,
  throwError,
} from 'utils/helpers';
import { isValidElement } from 'react';
import { getStorageAppItem } from 'stores/app';
import { getStaticLocaleFromApp } from 'utils/localeUtils';
import { API, apiUrl } from 'constants/Api';
import useZustand from 'hooks/useZustand';
import authService from 'services/authService';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import consoleX from 'utils/consoleX';
import { copyToClipboard } from 'utils/copyToClipboard';

// *******************************************************************************
// MAIN
// *******************************************************************************
const appService = {
  appInstance: null,
  _token: null, // theme tokens injected from AppLayout
  _modals: new Map(), // registry for modal controllers by key
  _imperativeModalCleanups: [], // registry of close functions for createRoot imperative modals
};

appService.initAppInstance = instance => {
  appService.appInstance = instance;
  return appService;
};

// allow AppLayout to provide theme tokens
appService.setThemeTokens = token => {
  appService._token = token || null;
  return appService;
};

// appService.getGpsByAddress = async data => {
//   const url = `v1/geolocation/get-gps-by-address`;
//   const [doc] = await axiosService.post({ url, data });
//   return doc?.[0] && doc?.[1] ? doc : null;
// };

/**
 * Device detection based on viewport width (Ant Design breakpoints)
 * - Mobile: < 768px
 * - Tablet: 768px - 991px
 * - Desktop: >= 992px
 */
appService.isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
};

appService.isTablet = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 768 && window.innerWidth < 992;
};

appService.isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth >= 992;
};

appService.getDevice = () => {
  if (appService.isMobile()) return 'mobile';
  if (appService.isTablet()) return 'tablet';
  return 'desktop';
};

appService.getQuote = async (author = null) => {
  const url = author
    ? apiUrl.build({ url: API.TOOLS.GET_QUOTE, params: { author } })
    : apiUrl.build({ url: API.TOOLS.GET_QUOTE });

  const [doc] = await axiosService.get({
    url,
    returnAsObject: true,
    headers: { 'x-forcerequest': true },
  });
  return doc;
};

appService.getServerVersion = async () => {
  const [doc] = await axiosService.get({
    url: API.TOOLS.GET_SERVER_VERSION,
    returnAsObject: true,
    headers: { 'x-forcerequest': true },
  });
  return doc;
};

// ─────────────────────────────────────────────────────────────
// MODAL API (context-aware via App.useApp())
// ─────────────────────────────────────────────────────────────
appService.modal = (config = {}) => {
  const { modal } = appService.appInstance || {};
  if (!modal) {
    console.error('appService.appInstance.modal not available');
    return;
  }

  let {
    flag = 'info', // 'info' | 'success' | 'error' | 'warning' | 'confirm' | aliases
    title: mdlTitle = null, // string | ReactNode
    content: Content = null, // string i18n key | ReactNode
    okText = 'ok',
    cancelText = 'cancel',
    customButtons: CustomButtons = null, // ReactNode extra (rendered before default buttons)
    onOk,
    onCancel,
    icon, // ReactNode opcional (undefined = use default icon, null = no icon)
    key,
    centered = true,
    maskClosable = false,
    okButtonProps,
    cancelButtonProps,
    // outras props do Modal do AntD (width, className, etc)
    ...rest
  } = config;

  // allow flag as ReactElement (becomes icon)
  if (isValidElement(flag)) {
    icon = flag;
    flag = 'info';
  }

  // mapear aliases
  const method = resolveModalMethod(flag);

  // translate only when not ReactNode
  const titleNode = isValidElement(mdlTitle) ? mdlTitle : translateX(mdlTitle);
  const contentNode = isValidElement(Content) ? Content : translateX(Content);
  const okTextNode = isValidElement(okText) ? okText : translateX(okText);
  const cancelTextNode = isValidElement(cancelText) ? cancelText : translateX(cancelText);

  // Build button props - let Ant Design/ConfigProvider handle styling natively
  // This preserves hover effects and native button behavior
  const resolvedOkProps = {
    type: 'primary', // default to primary
    ...okButtonProps, // spread user props (danger, icon, style, etc.)
  };

  const resolvedCancelProps = {
    type: 'default',
    ...cancelButtonProps,
  };

  // montar footer custom SOMENTE se CustomButtons foi explicitamente fornecido
  const footer = CustomButtons
    ? (_, { OkBtn, CancelBtn }) => (
        <>
          {CustomButtons}
          <CancelBtn {...resolvedCancelProps} />
          <OkBtn {...resolvedOkProps} />
        </>
      )
    : undefined;

  // abrir via modal API do App (context-aware)
  const controller = modal[method]({
    title: titleNode,
    content: contentNode,
    okText: okTextNode,
    cancelText: cancelTextNode,
    // Pass icon if explicitly provided (including null to remove default icon)
    ...(icon !== undefined && { icon }),
    centered,
      mask: { closable: maskClosable },
    onOk,
    onCancel,
    // when no custom footer, still inject props into buttons
    okButtonProps: footer ? undefined : resolvedOkProps,
    cancelButtonProps: footer ? undefined : resolvedCancelProps,
    ...rest,
  });

  // persistir controller por key se fornecida
  if (key) appService._modals.set(key, controller);

  // public controller compatible with notification/message
  return {
    close: () => {
      try {
        controller?.destroy?.();
        if (key) appService._modals.delete(key);
      } catch {}
    },
    update: (patch = {}) => {
      try {
        // re-normaliza campos i18n quando string
        if (typeof patch.okText === 'string') patch.okText = translateX(patch.okText);
        if (typeof patch.cancelText === 'string') patch.cancelText = translateX(patch.cancelText);
        if (typeof patch.title === 'string') patch.title = translateX(patch.title);
        if (typeof patch.content === 'string') patch.content = translateX(patch.content);

        controller?.update?.(patch);
      } catch {}
    },
  };
};

appService.modalUpdate = (key, patch = {}) => {
  const ctl = appService._modals.get(key);
  if (!ctl) {
    console.error(`modalUpdate: no modal found for key '${key}'`);
    return;
  }
  try {
    if (typeof patch.okText === 'string') patch.okText = translateX(patch.okText);
    if (typeof patch.cancelText === 'string') patch.cancelText = translateX(patch.cancelText);
    if (typeof patch.title === 'string') patch.title = translateX(patch.title);
    if (typeof patch.content === 'string') patch.content = translateX(patch.content);
    ctl.update?.(patch);
  } catch {}
};

appService.modalDestroy = key => {
  if (!key) {
    appService._modals.forEach(ctl => ctl?.close?.());
    appService._modals.clear();
    return;
  }
  const ctl = appService._modals.get(key);
  if (!ctl) return;
  try {
    ctl.close?.();
  } finally {
    appService._modals.delete(key);
  }
};

// Register a cleanup function for a createRoot imperative modal.
// Returns an unregister function to be called when the modal closes normally.
appService.registerImperativeModal = cleanupFn => {
  appService._imperativeModalCleanups.push(cleanupFn);
  return () => {
    const idx = appService._imperativeModalCleanups.indexOf(cleanupFn);
    if (idx !== -1) appService._imperativeModalCleanups.splice(idx, 1);
  };
};

// Force-close all open modals: both keyed (appService.modal) and imperative (createRoot).
// Called on forced logout or session expiry.
appService.destroyAllModals = () => {
  // Close all keyed modals (appService.modal() with key)
  appService.modalDestroy();

  // Close all registered imperative (createRoot) modals
  const cleanups = [...appService._imperativeModalCleanups];
  appService._imperativeModalCleanups = [];
  cleanups.forEach(fn => {
    try {
      fn();
    } catch {}
  });
};

// ─────────────────────────────────────────────────────────────
// NOTIFICATION
// ─────────────────────────────────────────────────────────────
appService.notification = (config = {}) => {
  const { notification } = appService.appInstance || {};
  if (!notification) {
    // Warn but don't block - notification API may not be ready during early app initialization
    console.warn('appService.notification: API not available yet (app initializing)');
    return;
  }

  let {
    flag = 'info',
    title: notificationTitle = null,
    description: notificationDescription = null,
    key: notificationKey = `notification-key-${+new Date()}`,
    duration: notificationDuration = 4,
    replaceFrom: replaceFromArray = [],
    replaceTo: replaceToArray = [],
    icon = null,
    placement = 'topRight',
    bottom = 24,
    className,
    noTranslateTitle = false, // Skip i18n translation for title
    noTranslateDescription = false, // Skip i18n translation for description
    actions = null, // Array of button configs: [{ text, onClick, type, danger, icon }]
    btn = null, // Direct ReactNode for buttons (Ant Design native)
    progress = false, // Show progress bar (default false)
    ...restOptions
  } = config;

  if (!notificationKey) notificationKey = genKey('ntf');

  if (isValidElement(flag)) {
    icon = flag;
    flag = 'info';
  } else {
    // Normalize flag aliases
    const normalizedFlag = String(flag).toLowerCase();

    if (['s', 'success'].includes(normalizedFlag)) {
      flag = 'success';
    } else if (['e', 'err', 'error', 'f', 'failure'].includes(normalizedFlag)) {
      flag = 'error';
    } else if (['w', 'warn', 'warning'].includes(normalizedFlag)) {
      flag = 'warning';
    } else if (['i', 'info'].includes(normalizedFlag)) {
      flag = 'info';
    } else if (['l', 'loading'].includes(normalizedFlag)) {
      flag = 'loading';
    } else if (!['success', 'error', 'warning', 'info', 'loading'].includes(flag)) {
      console.error(`Unknown type '${flag}' in notification()`);
      return;
    }
  }

  // Build action buttons if actions array is provided
  const buildActionButtons = (actionsConfig, notifKey) => {
    if (!actionsConfig || !Array.isArray(actionsConfig) || actionsConfig.length === 0) {
      return null;
    }

    return (
      <Space>
        {actionsConfig.map((action, index) => (
          <Button
            key={action.key || index}
            type={action.type || 'default'}
            size={action.size || 'small'}
            danger={action.danger}
            icon={action.icon}
            style={{ minWidth: action.minWidth ?? 80, ...action.style }}
            onClick={() => {
              action.onClick?.();
              // Auto-close notification after button click unless explicitly disabled
              if (action.closeOnClick !== false) {
                appService.notificationDestroy(notifKey);
              }
            }}
          >
            {action.noTranslate ? action.text : translateX(action.text)}
          </Button>
        ))}
      </Space>
    );
  };

  const openNotification = (patch = {}) => {
    const finalFlag = patch.flag ?? flag;
    const finalTitle = patch.title ?? notificationTitle;
    const finalDescription = patch.description ?? notificationDescription;
    const finalReplaceFrom = patch.replaceFrom ?? replaceFromArray;
    const finalReplaceTo = patch.replaceTo ?? replaceToArray;
    const finalNoTranslateTitle = patch.noTranslateTitle ?? noTranslateTitle;
    const finalNoTranslateDescription = patch.noTranslateDescription ?? noTranslateDescription;
    const finalActions = patch.actions ?? actions;
    const finalBtn = patch.btn ?? btn;
    const finalKey = patch.key ?? notificationKey;

    // Translate only if not skipped and not a React element
    const translatedTitle =
      finalNoTranslateTitle || isValidElement(finalTitle)
        ? finalTitle
        : translateX(finalTitle, finalReplaceFrom, finalReplaceTo);

    const translatedDescription =
      finalNoTranslateDescription || isValidElement(finalDescription)
        ? finalDescription
        : translateX(finalDescription, finalReplaceFrom, finalReplaceTo);

    // Build buttons: prefer direct btn, then build from actions array
    const buttonElement = finalBtn || buildActionButtons(finalActions, finalKey);

    const finalProgress = patch.progress ?? progress;

    const payload = {
      title: translatedTitle,
      description: translatedDescription,
      key: finalKey,
      duration: Number.isFinite(patch.duration ?? notificationDuration)
        ? (patch.duration ?? notificationDuration)
        : 4,
      placement: patch.placement ?? placement,
      bottom: patch.bottom ?? bottom,
      icon: patch.icon ?? icon,
      className: patch.className ?? className,
      actions: buttonElement,
      showProgress: finalProgress,
      pauseOnHover: finalProgress, // Only pause on hover when progress is shown
      ...restOptions,
      ...patch,
    };

    // Ant Design notification doesn't have .loading() method
    // Use .open() for loading or fallback to .info()
    const notificationMethod = finalFlag === 'loading' ? 'info' : finalFlag;
    notification[notificationMethod](payload);
  };

  openNotification();

  return {
    close: () => appService.notificationDestroy(notificationKey),
    update: (patch = {}) => openNotification(patch),
  };
};

appService.notificationUpdate = (key, patch) => appService.notification({ key, ...patch });

appService.notificationDestroy = key => {
  const { notification } = appService.appInstance || {};
  // Silently return if notification API not yet available (e.g., during app initialization)
  if (!notification) return;
  return key ? notification.destroy(key) : notification.destroy();
};

// ─────────────────────────────────────────────────────────────
// MESSAGE
// ─────────────────────────────────────────────────────────────
appService.message = async (config = {}) => {
  const { message } = appService.appInstance || {};
  if (!message) {
    console.error('appService.appInstance.message not available');
    return;
  }

  let {
    flag = 'info',
    text: msgText = null,
    key: msgKey,
    delay,
    duration,
    replaceFrom: rf = [],
    replaceTo: rt = [],
    icon = null,
    className = 'message-info',
    awaitPaint = true,
    minVisibleMs = 300,
    awaitClose = false,
    ...restOptions
  } = config;

  // Use nullish coalescing to allow 0 as valid duration (means "stay forever")
  const msgDuration = delay ?? duration ?? 2;

  if (!msgKey) msgKey = genKey('msg');

  // Helper to normalize flag shorthand to full name
  const normalizeFlag = f => {
    if (isValidElement(f)) return 'info';
    switch (String(f).toLowerCase()) {
      case 's':
      case 'success':
        return 'success';
      case 'e':
      case 'err':
      case 'error':
      case 'f':
      case 'failure':
        return 'error';
      case 'w':
      case 'warning':
      case 'warn':
        return 'warning';
      case 'i':
      case 'info':
        return 'info';
      case 'l':
      case 'loading':
        return 'loading';
      default:
        if (!['success', 'error', 'warning', 'info', 'loading'].includes(f)) {
          console.error(`Unknown type '${f}' in message() helper function!`);
          return null;
        }
        return f;
    }
  };

  if (isValidElement(flag)) {
    icon = flag;
    flag = 'info';
  } else {
    flag = normalizeFlag(flag);
    if (!flag) return;
  }

  const normalizedDuration = Number.isFinite(msgDuration) ? msgDuration : 2;

  const thenable = message.open({
    type: flag,
    content: translateX(msgText, rf, rt),
    key: msgKey,
    duration: normalizedDuration,
    icon,
    className,
    ...restOptions,
    style: {
      // marginTop: '20vh',
      ...(restOptions?.style || {}),
    },
  });

  if (awaitPaint) {
    await nextFrame();
  }
  if (minVisibleMs > 0) {
    await sleep(minVisibleMs);
  }

  const controller = {
    close: () => message.destroy(msgKey),
    update: (patch = {}) => {
      // Normalize the flag if provided (e.g., 's' -> 'success')
      const patchFlag = patch.flag ? normalizeFlag(patch.flag) : flag;
      // Use nullish coalescing to allow 0 as valid duration
      // Default to 2 on update (not the original duration) when not specified
      const patchDuration = patch.delay ?? patch.duration ?? 2;

      return message.open({
        type: patchFlag,
        key: msgKey,
        content: translateX(patch.text ?? msgText, patch.replaceFrom ?? rf, patch.replaceTo ?? rt),
        duration: Number.isFinite(patchDuration) ? patchDuration : 2,
        icon: patch.icon ?? icon,
        className: patch.className ?? className,
        ...restOptions,
        ...patch,
        style: {
          // marginTop: '20vh',
          ...(restOptions?.style || {}),
          ...(patch?.style || {}),
        },
      });
    },
    thenable,
  };

  if (awaitClose) {
    await thenable;
    return controller;
  }

  return controller;
};

appService.messageDestroy = key => {
  const { message } = appService.appInstance || {};
  if (!message) {
    console.error('appService.appInstance.message not available');
    return;
  }
  return key ? message.destroy(key) : message.destroy();
};

function sleep(ms = 0) {
  return new Promise(res => setTimeout(res, ms));
}

function nextFrame() {
  return new Promise(res => {
    if (typeof window === 'undefined' || !window.requestAnimationFrame) {
      setTimeout(res, 0);
    } else {
      window.requestAnimationFrame(() => res());
    }
  });
}

// appService.loadApplicationParameters = async () => {
//   try {
//     const [docs] = await axiosService.get({
//       url: API_PARAMETERS,
//       returnAsObject: true,
//     });
//     return clone(docs);
//   } catch (error) {
//     throw error;
//   }
// };

appService.console = (flag, message, details, emoji, dividerTopChar, dividerBottomChar) => {
  // Skip logging if not in a log-enabled environment (localhost or homologation)
  if (!shouldShowLogs()) return;

  const icon = $icon();
  const method = $method();
  const color = $color();

  let text = icon ? `${icon}${message ? ' ' : ''}${message}` : message;

  if (dividerTopChar && typeof dividerBottomChar === 'undefined') {
    dividerBottomChar = dividerTopChar;
  }

  if (dividerTopChar) console[method](color, divider(dividerTopChar));
  if (message) {
    console[method](color, `${dayjs().format('HH:mm:ss.SSS')} ${text}`);
  }
  if (details) {
    console[method](details);
  }
  if (dividerBottomChar) console[method](color, divider(dividerBottomChar));

  function $color() {
    const red = '\x1b[31m%s\x1b[0m';
    const green = '\x1b[32m%s\x1b[0m';
    const yellow = '\x1b[33m%s\x1b[0m';
    const blue = '\x1b[34m%s\x1b[0m';
    const cyan = '\x1b[36m%s\x1b[0m';

    if (!flag || ['start', 'i'].includes(flag)) return blue;
    if (['s', 'ok'].includes(flag)) return green;
    if (['e', 'no'].includes(flag)) return red;
    if (['w'].includes(flag)) return yellow;
    if (['t'].includes(flag)) return cyan;
  }

  function $method() {
    if (!flag || ['start', 'i'].includes(flag)) return 'log';
    if (['s', 'ok'].includes(flag)) return 'log';
    if (['e', 'no'].includes(flag)) return 'error';
    if (['w'].includes(flag)) return 'warn';
    if (['t'].includes(flag)) return 'trace';
  }

  function $icon() {
    if (emoji) return emoji;
    switch (flag) {
      case 'start':
        return '🚀';
      case 's':
        return '✅';
      case 'e':
        return '❌';
      case 'ok':
        return '👍';
      case 'no':
        return '👎';
      case 'w':
        return '⚠️';
      case 'i':
        return '️🕑';
      default:
        return '';
    }
  }
};

// Legacy alias for backward compatibility
appService.consoleTitle = consoleX;

appService.log = (text, show) => {
  if (show) consoleX(text);
};

appService.getLocale = () => {
  // Priority: localStorage -> app.LOCALE -> fallback 'US'
  try {
    const stored = getStorageAppItem('countryCode');
    if (stored) return stored;
  } catch (e) {
    // ignore if stores/app is not available yet
  }

  // Extract country code from app.LOCALE (e.g., 'pt' -> 'BR', 'US' -> 'US')
  const locale = app.LOCALE || 'US';
  return getStaticLocaleFromApp(locale, true); // revert=true to get country code
};

appService.getLogo = (isDarkTheme = false, logoType = null, small = false) => {
  const logoAppIcon = app.LOGO_APP_ICON;
  const logoAppIconWhite = app.LOGO_APP_ICON_WHITE;

  const logoApp = app.LOGO_APP;
  const logoAppWhite = app.LOGO_APP_WHITE;

  const logoLoginSide = app.LOGO_LOGIN_SIDE;

  const logo = app.LOGO;
  const logoWhite = app.LOGO_WHITE;

  if (isDarkTheme) {
    if (logoType === 'menu') {
      return small ? logoAppIconWhite : logoAppWhite;
    } else if (logoType === 'login-side') {
      return logoLoginSide;
    } else {
      return logoWhite;
    }
  } else {
    if (logoType === 'menu') {
      return small ? logoAppIcon : logoApp;
    } else if (logoType === 'login-side') {
      return logoLoginSide;
    } else {
      return logo;
    }
  }
};

appService.buildAttachmentIds = async (attachmentsArray, document) => {
  if (!attachmentsArray || attachmentsArray?.length === 0) return document?.attachmentIds;

  const attachments = Array.isArray(attachmentsArray) ? attachmentsArray : [attachmentsArray];

  return await appService.persistAttachments(attachments, document?.attachmentIds);
};

appService.persistAttachments = async (attachments, attachmentIds = []) => {
  for (const a of attachments) {
    if (a.action === 'add') {
      const axiosOptions = {
        url: API.ATTACHMENTS.UPLOAD,
        _id: a._id,
        data: { persisted: true },
      };
      await axiosService.put(axiosOptions);

      attachmentIds.push(a._id);
    } else if (a.action === 'remove') {
      const axiosOptions = {
        url: API.ATTACHMENTS.UPLOAD,
        _id: a._id,
        headers: { 'x-forcepermanentlydeletation': true },
      };
      await axiosService.delete(axiosOptions);

      const storageRef = ref(storage, a?.uri);
      try {
        await deleteObject(storageRef);
      } catch (e) {
        logErrorSilent(e, {
          context: 'appService.deleteAttachment (Firebase deleteObject)',
          extraData: { uri: a?.uri, attachmentId: a?._id },
        });
      }
      attachmentIds = attachmentIds.filter(attachId => attachId !== a._id);
    }
  }

  return attachmentIds;
};

appService.parseAttachmentsState = (attachments, attachmentIds = []) => {
  const toRemove = attachments?.filter(a => a?.action === 'remove')?.map(a => a._id);
  const toAdd = attachments?.filter(a => a?.action !== 'remove')?.map(a => a._id);
  const filteredAttachments = attachmentIds?.filter(a => !toRemove.includes(a));
  return [...new Set([...filteredAttachments, ...toAdd])];
};

appService.getAttachmentUrlByTag = (tag, attachments) => {
  const found = attachments?.find(x => x?.tags?.includes(tag) && x?.action === 'add');
  if (found) {
    return found.url;
  } else {
    const found = attachments?.find(x => x?.tags?.includes(tag) && x?.action === 'remove');
    if (found) return '';
  }
  return false;
};

appService.copyToClipboard = async (url = window.location.href, copyType = 'url') => {
  if (!navigator.clipboard) {
    appService.notification(
      'w',
      'incompatible_browser',
      'copyUrl',
      copyType === 'url' ? 'url_copy_not_supported' : 'link_copy_not_supported',
      10
    );
    return;
  }

  try {
    await navigator.clipboard.writeText(url);
    appService.notification({ flag: 's', title: copyType === 'url' ? 'url_copied' : 'link_copied' });
  } catch {
    appService.notification({ flag: 'f', title: copyType === 'url' ? 'url_not_copied' : 'link_not_copied' });
  }
};

appService.getBackendUrl = () => {
  // Build backend URL based on CONNECTION and HOMOLOGATION_MODE
  const isLocal = app.CONNECTION === 'local';
  const protocol = isLocal ? 'http' : 'https';
  const host = isLocal ? app.API_HOST_LOCALHOST : app.API_HOST_PRODUCTION;
  const port = app.HOMOLOGATION_MODE ? app.API_PORT_HOMOLOGATION : app.API_PORT_PRODUCTION;
  return `${protocol}://${host}:${port}`;
};

appService.handleCopyToClipboard = (value, message) => {
  if (!value) return;
  // copyToClipboard(value, message || 'data_copied');
  copyToClipboard(value, message);
};

appService.updateAppPreferences = async (config = {}) => {
  const storedUser = useZustand('auth', 'user', false);
  const { category, data, ignoreSubscription = true } = config;

  // Validate user
  if (!storedUser?._id) throwError('User authentication is required');

  // Validate category
  if (!category) {
    throwError('config.category is required');
  }

  // Validate data object
  if (!data || Object?.keys(data)?.length === 0) {
    throwError('config.data object cannot be empty');
  }

  const payload = {
    scope: 'application',
    category,
    data,
    ignoreSubscription,
  };

  const axiosOptions = {
    url: API.PARAMETERS.UPDATE,
    data: payload,
  };

  await axiosService.post(axiosOptions);
};

appService.getAppPreferences = (config = {}) => {
  const storedParameters = useZustand('app', 'parameters', false);

  const { category, key } = config;

  // Case 1: Get entire application preferences
  if (!category && !key) {
    return storedParameters?.application;
  }

  // Case 2: Get entire category
  if (category && !key) {
    return storedParameters?.application?.[category];
  }

  // Case 3: Get specific key from specific category
  if (category && key) {
    return storedParameters?.application?.[category]?.[key];
  }

  // Case 4: Search for key in all categories (no category specified)
  if (!category && key) {
    const appPrefs = storedParameters?.application;
    if (!appPrefs) return undefined;

    // Search through all categories and return first match
    for (const catName of Object.keys(appPrefs)) {
      if (appPrefs[catName]?.[key] !== undefined) {
        return appPrefs[catName][key];
      }
    }

    return undefined;
  }

  return undefined;
};

appService.updateModulePreferences = async (config = {}) => {
  const storedUser = useZustand('auth', 'user', false);
  const storedModule = useZustand('module', 'config', false);
  const storedParameters = useZustand('app', 'parameters', false);
  const setParameters = useZustand('app', 'setParameters', false);
  const { category, data, ignoreSubscription = false } = config;
  // private: when true, parameter is user-specific (uses createdByAccountId in query)
  // private: when false (default), parameter is shared across subscription
  const isPrivate = config.private ?? false;
  let { moduleId } = config;

  // Validate user
  if (!storedUser?._id) throwError('User authentication is required');

  // Validate module
  if (!moduleId) moduleId = storedModule?.id;
  if (!moduleId) throwError('config.moduleId is required');

  // Validate category
  if (!category) {
    throwError('config.category is required');
  }

  // Validate data (object or array)
  if (!data || (Array.isArray(data) ? data.length === 0 : Object?.keys(data)?.length === 0)) {
    throwError('config.data cannot be empty');
  }

  const payload = {
    scope: 'module',
    moduleId,
    category,
    data,
    ignoreSubscription,
    private: isPrivate,
  };

  const axiosOptions = {
    url: API.PARAMETERS.UPDATE,
    data: payload,
  };

  // Update local store immediately (optimistic update)
  if (storedParameters && setParameters) {
    const updatedParams = clone(storedParameters);
    if (!updatedParams.modules) updatedParams.modules = {};
    if (!updatedParams.modules[moduleId]) updatedParams.modules[moduleId] = {};

    // Handle arrays: replace category value entirely
    // Handle objects: merge data into existing category
    if (Array.isArray(data)) {
      updatedParams.modules[moduleId][category] = data;
    } else {
      if (!updatedParams.modules[moduleId][category]) updatedParams.modules[moduleId][category] = {};
      Object.assign(updatedParams.modules[moduleId][category], data);
    }
    setParameters(updatedParams);
  }

  // Send to backend immediately (awaitable — caller can await this function for critical saves)
  axiosService.post(axiosOptions);
};

appService.getModulePreferences = (config = {}) => {
  const storedModule = useZustand('module', 'config', false);
  const storedParameters = useZustand('app', 'parameters', false);

  const { moduleId, category, key } = config;
  const targetModuleId = moduleId || storedModule?.id;

  // Case 1: Get entire module preferences
  if (!category && !key) {
    return storedParameters?.modules?.[targetModuleId];
  }

  // Case 2: Get the entire category
  if (category && !key) {
    return storedParameters?.modules?.[targetModuleId]?.[category];
  }

  // Case 3: Get specific key from specific category
  if (category && key) {
    return storedParameters?.modules?.[targetModuleId]?.[category]?.[key];
  }

  // Case 4: Search for key in all categories (no category specified)
  if (!category && key) {
    const modulePrefs = storedParameters?.modules?.[targetModuleId];
    if (!modulePrefs) return undefined;

    // Search through all categories and return first match
    for (const catName of Object.keys(modulePrefs)) {
      if (modulePrefs[catName]?.[key] !== undefined) {
        return modulePrefs[catName][key];
      }
    }

    return undefined;
  }

  return undefined;
};

appService.timeZones = timezones.sort((a, b) => sort(a, b, 'offset'));
appService.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
appService.locationTimezone = timezones.find(tz => tz.utc.includes(appService.timeZone)) || {};

appService.checkParameterInUse = async (config = {}) => {
  const { module, key, _id } = config;

  if (!module || !key || !_id) {
    console.error('[appService.checkParameterInUse] Missing required params:', {
      module,
      key,
      _id,
    });
    return false;
  }

  try {
    const url = mergeUrlParamsQuery({
      url: '/v1/parameters/:module/:key/:_id',
      params: { module, key, _id },
    });

    const [response] = await axiosService.get({
      url,
      returnAsObject: true,
    });

    // API returns true if in use, false if not
    return response?.inUse === true;
  } catch (error) {
    console.error('[appService.checkParameterInUse] Error:', error);
    // Default to true (safe) to prevent accidental deletion
    return true;
  }
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default appService;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function resolveModalMethod(flag) {
  const f = String(flag || '').toLowerCase();
  if (['confirm', 'q', 'c', 'question'].includes(f)) return 'confirm';
  if (['success', 's', 'ok'].includes(f)) return 'success';
  if (['error', 'e', 'no', 'failure', 'f'].includes(f)) return 'error';
  if (['warning', 'w', 'warn'].includes(f)) return 'warning';
  return 'info';
}
